import { sortThread } from '../lib/commentThread.ts';
import { formatDateOnly } from '../lib/expiry.ts';
import type { SubmissionComment } from '../lib/types.ts';

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function Bubble({ comment }: { comment: SubmissionComment }) {
  const isAdmin = comment.author_type === 'admin';
  return (
    <div className={`max-w-[80%] ${isAdmin ? '' : 'self-end'}`}>
      <div className={`mb-1.5 flex items-center gap-2 ${isAdmin ? '' : 'justify-end'}`}>
        {isAdmin && <span className="rounded bg-slate-600 px-2 py-0.5 text-[11px] font-bold text-white">Reviewer</span>}
        <span className="text-[11px] text-slate-400">{formatTime(comment.created_at)}</span>
        {!isAdmin && (
          <span className="rounded px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: 'var(--brand-primary)' }}>
            Vendor
          </span>
        )}
      </div>
      {comment.body && (
        <div
          className={`px-3.5 py-2.5 text-sm leading-5 ${isAdmin ? 'rounded-[4px_14px_14px_14px] bg-slate-100 text-slate-700' : 'rounded-[14px_4px_14px_14px] text-white'}`}
          style={isAdmin ? undefined : { background: 'var(--brand-primary)' }}
        >
          <div className="whitespace-pre-wrap">{comment.body}</div>
        </div>
      )}
      {comment.attachments.length > 0 && (
        <div className="mt-1.5 flex flex-col gap-1.5">
          {comment.attachments.map((a) => (
            <div key={a.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4D148C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
              </svg>
              <div className="min-w-0 flex-1">
                {a.attachment_url ? (
                  <a href={a.attachment_url} target="_blank" rel="noopener noreferrer" className="block truncate text-xs font-semibold text-[#4D148C] hover:underline">{a.attachment_name}</a>
                ) : (
                  <span className="block truncate text-xs font-semibold text-slate-600">{a.attachment_name}</span>
                )}
                <span className="text-[11px] text-slate-400">
                  {a.is_answer ? 'Document' : 'Reference'}
                  {a.attachment_expires_at ? ` · expires ${formatDateOnly(a.attachment_expires_at, {}, 'en-AU')}` : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CommentThread({ comments }: { comments: SubmissionComment[] }) {
  const sorted = sortThread(comments);
  if (sorted.length === 0) return <p className="text-xs text-slate-400">No messages yet.</p>;
  return (
    <div className="flex flex-col gap-4">
      {sorted.map((c) => (
        <Bubble key={c.id} comment={c} />
      ))}
    </div>
  );
}
